<template>
  <div class="companies-module">
    <v-card outlined class="info-card">
      <v-card-title class="info-title d-flex align-center">
        <v-icon size="18" class="mr-2">mdi-domain</v-icon>
        {{ $t("companies.title") }}
        <v-spacer></v-spacer>
        <v-tooltip location="bottom">
          <template #activator="{ props }">
            <v-btn
              icon
              variant="text"
              v-bind="props"
              @click="toggleViewMode"
              :title="viewMode === 'cards' ? $t('companies.view.table') : $t('companies.view.cards')"
            >
              <v-icon>{{ viewMode === "cards" ? "mdi-view-list" : "mdi-view-module" }}</v-icon>
            </v-btn>
          </template>
          {{ viewMode === 'cards' ? $t('companies.view.table') : $t('companies.view.cards') }}
        </v-tooltip>
        <v-btn color="primary" @click="openAddDialog">
          <v-icon size="18" start>mdi-plus</v-icon>
          {{ $t("companies.actions.add") }}
        </v-btn>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text>
        <div class="controls-row">
          <v-text-field
            v-model="search"
            :label="$t('companies.search')"
            prepend-inner-icon="mdi-magnify"
            hide-details
            class="search-field"
          ></v-text-field>
        </div>

        <div v-if="viewMode === 'cards'" class="companies-grid">
          <v-row dense>
            <v-col
              v-for="company in filteredCompanies"
              :key="company.id"
              cols="12"
              md="6"
              lg="4"
              xl="3"
            >
              <v-card
                class="company-card"
                outlined
                tabindex="0"
                role="button"
                @click="openCompanyDetails(company)"
                @keydown.enter.prevent="openCompanyDetails(company)"
                @keydown.space.prevent="openCompanyDetails(company)"
              >
                <v-card-text class="company-card__content">
                  <div class="company-card__header">
                    <div class="company-card__identity">
                      <div class="company-card__icon">
                        <v-icon size="20">mdi-domain</v-icon>
                      </div>
                      <div class="company-card__heading">
                        <div class="company-card__name">
                          {{ company.company_name }}
                        </div>
                        <div class="company-card__administrator">
                          {{ $t("companies.fields.administrator") }}:
                          {{ getCompanyDisplayValue(company.administrator) }}
                        </div>
                      </div>
                    </div>
                    <v-chip
                      :color="company.status === 1 ? 'success' : 'neutral'"
                      class="company-card__status"
                    >
                      {{ company.status === 1 ? $t('companies.values.active') : $t('companies.values.inactive') }}
                    </v-chip>
                  </div>

                  <div class="company-card__chips">
                    <v-chip size="x-small" prepend-icon="mdi-pound">
                      {{ $t("companies.fields.id") }} {{ company.id }}
                    </v-chip>
                    <v-chip size="x-small" color="primary" prepend-icon="mdi-shield-check-outline">
                      {{ getCompanyLicensePackageLabel(company) }}
                    </v-chip>
                  </div>

                  <div class="company-card__details">
                    <div class="company-detail-row">
                      <span class="company-detail-row__label">{{ $t("companies.fields.fiscalCode") }}</span>
                      <span class="company-detail-row__value">{{ getCompanyDisplayValue(company.fiscal_code) }}</span>
                    </div>
                    <div class="company-detail-row">
                      <span class="company-detail-row__label">{{ $t("companies.fields.phone") }}</span>
                      <span class="company-detail-row__value">{{ getCompanyDisplayValue(company.phone) }}</span>
                    </div>
                    <div class="company-detail-row">
                      <span class="company-detail-row__label">{{ $t("companies.fields.email") }}</span>
                      <span class="company-detail-row__value">{{ getCompanyDisplayValue(company.email) }}</span>
                    </div>
                  </div>

                  <div class="company-card__license-panel">
                    <div class="company-card__section-label">
                      {{ $t("companies.form.licenseSection") }}
                    </div>
                    <div class="company-card__license-package">
                      {{ getCompanyLicensePackageLabel(company) }}
                    </div>
                    <div class="company-card__license-expiry">
                      <v-icon size="14">mdi-calendar-range</v-icon>
                      <span>
                        {{ $t("companies.fields.licenseExpiresAt") }}:
                        {{ getCompanyLicenseExpiryLabel(company) }}
                      </span>
                    </div>
                  </div>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions class="company-card__actions">
                  <v-btn variant="text" size="small" @click.stop="openEditDialog(company)">
                    <v-icon size="18">mdi-pencil</v-icon>
                    {{ $t("app.actions.edit") }}
                  </v-btn>
                  <v-btn
                    variant="text"
                    size="small"
                    color="error"
                    @click.stop="confirmDeleteCompany(company.id)"
                  >
                    <v-icon size="18">mdi-delete</v-icon>
                    {{ $t("app.actions.delete") }}
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>
        </div>

        <div v-else>
          <v-data-table
            :headers="columns"
            :items="filteredCompanies"
            item-key="id"
            class="companies-table"
            hide-default-footer
          >
            <template #item="{ item }">
              <tr @click="openCompanyDetails(item)">
                <td>{{ item.id }}</td>
                <td>{{ item.company_name }}</td>
                <td>{{ getCompanyDisplayValue(item.fiscal_code) }}</td>
                <td>{{ getCompanyDisplayValue(item.administrator) }}</td>
                <td>{{ getCompanyDisplayValue(item.phone) }}</td>
                <td>{{ getCompanyDisplayValue(item.email) }}</td>
                <td>
                  <v-chip size="x-small" color="primary">
                    {{ getCompanyLicensePackageLabel(item) }}
                  </v-chip>
                </td>
                <td>
                  <span class="company-table__muted">{{ getCompanyLicenseExpiryLabel(item) }}</span>
                </td>
                <td>
                  <v-chip :color="item.status === 1 ? 'success' : 'default'">
                    {{ item.status === 1 ? $t('companies.values.active') : $t('companies.values.inactive') }}
                  </v-chip>
                </td>
                <td>
                  <div class="table-actions">
                    <v-btn icon variant="text" size="small" @click.stop="openEditDialog(item)">
                      <v-icon size="18">mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      variant="text"
                      size="small"
                      color="error"
                      @click.stop="confirmDeleteCompany(item.id)"
                    >
                      <v-icon size="18">mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </td>
              </tr>
            </template>
          </v-data-table>
        </div>
      </v-card-text>
    </v-card>

    <CompanyFormDialog
      v-model="editDialog"
      :is-edit="isEdit"
      :form-value="editCompany"
      :status-items="statusItems"
      :license-package-items="licensePackageItems"
      :license-packages-loading="licensePackagesLoading"
      :save-loading="saveLoading"
      :error-message="dialogError"
      @save="handleDialogSave"
    />

    <v-dialog v-model="deleteDialog" max-width="480px">
      <v-card>
        <v-card-title class="info-title">
          Confirmare ștergere
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <p class="mb-4">
            Pentru a șterge definitiv această companie, introduceți cuvântul
            <strong>DELETE</strong> în câmpul de mai jos.
          </p>
          <v-text-field
            v-model="deleteConfirmInput"
            label='Scrieți "DELETE" pentru confirmare'
            autofocus
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-btn variant="text" @click="closeDeleteDialog">Anulează</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="error"
            :disabled="deleteConfirmInput !== 'DELETE'"
            @click="performDeleteCompany"
          >
            Șterge
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="statusSnackbar.open" :color="statusSnackbar.color" timeout="5000">
      {{ statusSnackbar.message }}
      <template #actions>
        <v-btn color="white" variant="text" @click="statusSnackbar.open = false">
          {{ $t("app.actions.close") || $t("app.actions.cancel") }}
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import axios from "axios";
import { defineComponent } from "vue";
import { useI18n } from "vue-i18n";
import CompanyFormDialog from "@/components/CompanyFormDialog.vue";
import {
  DEFAULT_COMPANY_LICENSE_PACKAGE_KEY,
  buildCompanyForm,
  buildCompanyPayload,
  buildLicensePackageSelectItems,
  buildLicensePayload,
  createEmptyCompanyForm,
  formatCompanyLicenseExpiry,
  resolveLicensePackageLabelByKey,
} from "@/Utile/companyForm.mjs";

export default defineComponent({
  name: "Companies",
  components: { CompanyFormDialog },
  emits: ["open-company-details", "companies-changed"],
  data() {
    const { t } = useI18n();
    return {
      statusItems: [
        { title: t("companies.values.active"), value: 1 },
        { title: t("companies.values.inactive"), value: 0 },
      ],
      companiesData: [],
      availableLicensePackages: [],
      licensePackagesLoading: false,
      editDialog: false,
      deleteDialog: false,
      isEdit: false,
      saveLoading: false,
      dialogError: "",
      editCompany: createEmptyCompanyForm(),
      deleteTargetId: null,
      deleteConfirmInput: "",
      statusSnackbar: {
        open: false,
        color: "success",
        message: "",
      },
      search: "",
      viewMode: "cards",
      // Definim coloanele pentru DataTable în Vuetify 3
      columns: [
        { key: "id", title: t("companies.fields.id") },
        { key: "company_name", title: t("companies.form.name") },
        { key: "fiscal_code", title: t("companies.fields.fiscalCode") },
        { key: "administrator", title: t("companies.fields.administrator") },
        { key: "phone", title: t("companies.fields.phone") },
        { key: "email", title: t("companies.fields.email") },
        { key: "license_package_key", title: t("companies.fields.licensePackage") },
        { key: "license_expires_at", title: t("companies.fields.licenseExpiresAt") },
        { key: "status", title: t("companies.fields.status") },
        { key: "actions", title: t("companies.fields.actions") },
      ],
    };
  },
  computed: {
    licensePackageItems() {
      return buildLicensePackageSelectItems(
        this.availableLicensePackages,
        this.$i18n?.locale || "ro"
      );
    },
    filteredCompanies() {
      if (!this.search) return this.companiesData;
      const searchTerm = this.search.toLowerCase();
      return this.companiesData.filter((company) =>
        Object.values(company).some((val) =>
          String(val).toLowerCase().includes(searchTerm)
        )
      );
    },
  },
  created() {
    this.ensureLicensePackagesLoaded().catch((error) => {
      console.error("Eroare la preîncărcarea pachetelor de licență:", error);
    });
    this.fetchCompanies();
  },
  methods: {
    showStatusMessage(color, message) {
      this.statusSnackbar = {
        open: true,
        color,
        message,
      };
    },
    getDefaultLicensePackageKey() {
      return (
        this.availableLicensePackages.find((pkg) => Number(pkg?.is_default_for_new_companies) === 1)?.key ||
        DEFAULT_COMPANY_LICENSE_PACKAGE_KEY
      );
    },
    async ensureLicensePackagesLoaded(force = false) {
      if (!force && this.availableLicensePackages.length) return;
      this.licensePackagesLoading = true;
      try {
        const { data } = await axios.get("/license-packages");
        this.availableLicensePackages = Array.isArray(data) ? data : [];
      } catch (error) {
        console.error("Eroare la încărcarea pachetelor de licență:", error);
        throw error;
      } finally {
        this.licensePackagesLoading = false;
      }
    },
    async fetchCompanyById(companyId) {
      const { data } = await axios.get(`/candidati/companies/${companyId}`);
      return data || null;
    },
    async fetchCompanyLicense(companyId, { ignoreForbidden = false } = {}) {
      try {
        const { data } = await axios.get(`/companies/${companyId}/license`);
        return data || null;
      } catch (error) {
        if (
          error?.response?.status === 404 ||
          (ignoreForbidden && error?.response?.status === 403)
        ) {
          return null;
        }
        throw error;
      }
    },
    async attachCompanyLicenses(companies) {
      const list = Array.isArray(companies) ? companies : [];
      if (!list.length) return [];

      const licenseEntries = await Promise.all(
        list.map(async (company) => {
          try {
            const license = await this.fetchCompanyLicense(company.id, { ignoreForbidden: true });
            return [Number(company.id), license];
          } catch (error) {
            console.error(`Eroare la încărcarea licenței pentru compania ${company.id}:`, error);
            return [Number(company.id), null];
          }
        })
      );

      const licensesByCompanyId = new Map(licenseEntries);
      return list.map((company) => {
        const license = licensesByCompanyId.get(Number(company.id));
        return {
          ...company,
          license_package_key: String(license?.package_key || company?.license_package_key || "")
            .trim()
            .toLowerCase(),
          license_expires_at: license?.expires_at || company?.license_expires_at || "",
          license_status: license?.status || company?.license_status || "",
        };
      });
    },
    getCompanyLicensePackageLabel(company) {
      const packageKey =
        String(company?.license_package_key || "").trim().toLowerCase() ||
        this.getDefaultLicensePackageKey();
      return (
        resolveLicensePackageLabelByKey(
          this.availableLicensePackages,
          packageKey,
          this.$i18n?.locale || "ro"
        ) || this.$t("companies.values.noLicenseAssigned")
      );
    },
    getCompanyLicenseExpiryLabel(company) {
      return formatCompanyLicenseExpiry(
        company?.license_expires_at,
        this.$i18n?.locale || "ro",
        this.$t("companies.values.noExpiry")
      );
    },
    getCompanyDisplayValue(value) {
      if (value === null || value === undefined) {
        return this.$t("companies.values.notSet");
      }
      const normalized = String(value).trim();
      return normalized || this.$t("companies.values.notSet");
    },
    toggleViewMode() {
      this.viewMode = this.viewMode === "cards" ? "list" : "cards";
    },
    openCompanyDetails(company) {
      if (!company || !company.id) return;
      this.$emit("open-company-details", Number(company.id));
    },
    async openEditDialog(company) {
      if (!company?.id) return;
      this.dialogError = "";
      this.isEdit = true;
      try {
        await this.ensureLicensePackagesLoaded();
        const [companyData, licenseData] = await Promise.all([
          this.fetchCompanyById(company.id),
          this.fetchCompanyLicense(company.id),
        ]);
        this.editCompany = buildCompanyForm(companyData || company, licenseData, {
          defaultPackageKey: this.getDefaultLicensePackageKey(),
        });
        this.editDialog = true;
      } catch (error) {
        console.error("Eroare la încărcarea companiei pentru editare:", error);
        this.showStatusMessage("error", this.$t("companies.messages.loadEditError"));
      }
    },
    async openAddDialog() {
      this.dialogError = "";
      this.isEdit = false;
      try {
        await this.ensureLicensePackagesLoaded();
        this.editCompany = createEmptyCompanyForm({
          packageKey: this.getDefaultLicensePackageKey(),
        });
        this.editDialog = true;
      } catch (error) {
        console.error("Eroare la pregătirea dialogului de companie:", error);
        this.showStatusMessage("error", this.$t("companies.messages.loadPackagesError"));
      }
    },
    async saveCompanyLicense(companyId, formSnapshot) {
      await axios.put(`/companies/${companyId}/license`, buildLicensePayload(formSnapshot, {
        defaultPackageKey: this.getDefaultLicensePackageKey(),
      }));
    },
    async handleDialogSave(formSnapshot) {
      if (this.isEdit) {
        await this.updateCompany(formSnapshot);
        return;
      }
      await this.addCompany(formSnapshot);
    },
    async addCompany(formSnapshot) {
      this.saveLoading = true;
      this.dialogError = "";
      let createdCompanyId = null;
      let companyCreated = false;
      let licenseUpdated = false;
      try {
        const { data } = await axios.post("/candidati/companies", buildCompanyPayload(formSnapshot));
        createdCompanyId = Number(data?.id) || null;
        companyCreated = true;
        if (createdCompanyId) {
          await this.saveCompanyLicense(createdCompanyId, formSnapshot);
          licenseUpdated = true;
        }
        this.editDialog = false;
        await this.fetchCompanies();
        this.$emit("companies-changed", { companyId: createdCompanyId });
        this.showStatusMessage("success", this.$t("companies.messages.addSuccess"));
      } catch (error) {
        console.error("Eroare la adăugarea companiei:", error);
        if (companyCreated && !licenseUpdated && createdCompanyId) {
          this.editDialog = false;
          await this.fetchCompanies();
          this.$emit("companies-changed", { companyId: createdCompanyId });
          this.showStatusMessage("warning", this.$t("companies.messages.licensePartial"));
          return;
        }
        this.dialogError = this.$t("companies.messages.saveError");
      } finally {
        this.saveLoading = false;
      }
    },
    async updateCompany(formSnapshot) {
      if (!formSnapshot?.id) {
        this.dialogError = this.$t("companies.messages.missingId");
        return;
      }

      this.saveLoading = true;
      this.dialogError = "";
      let companyUpdated = false;
      let licenseUpdated = false;
      try {
        await axios.put(`/candidati/companies/${formSnapshot.id}`, buildCompanyPayload(formSnapshot));
        companyUpdated = true;
        await this.saveCompanyLicense(formSnapshot.id, formSnapshot);
        licenseUpdated = true;
        this.editDialog = false;
        await this.fetchCompanies();
        this.$emit("companies-changed", { companyId: Number(formSnapshot.id) });
        this.showStatusMessage("success", this.$t("companies.messages.updateSuccess"));
      } catch (error) {
        console.error("Eroare la actualizarea companiei:", error);
        if (companyUpdated && !licenseUpdated) {
          this.editDialog = false;
          await this.fetchCompanies();
          this.$emit("companies-changed", { companyId: Number(formSnapshot.id) });
          this.showStatusMessage("warning", this.$t("companies.messages.licensePartial"));
          return;
        }
        this.dialogError = this.$t("companies.messages.saveError");
      } finally {
        this.saveLoading = false;
      }
    },
    confirmDeleteCompany(id) {
      this.deleteTargetId = id;
      this.deleteConfirmInput = "";
      this.deleteDialog = true;
    },
    closeDeleteDialog() {
      this.deleteDialog = false;
      this.deleteTargetId = null;
      this.deleteConfirmInput = "";
    },
    performDeleteCompany() {
      if (this.deleteConfirmInput !== "DELETE" || !this.deleteTargetId) return;
      this.deleteCompany(this.deleteTargetId);
    },
    async deleteCompany(id) {
      try {
        await axios.delete(`/candidati/companies/${id}`);
        await this.fetchCompanies();
        this.closeDeleteDialog();
        this.$emit("companies-changed", { deletedCompanyId: Number(id) });
        this.showStatusMessage("success", this.$t("companies.messages.deleteSuccess"));
      } catch (error) {
        console.error("Eroare la ștergerea companiei:", error);
        this.showStatusMessage("error", this.$t("companies.messages.deleteError"));
      }
    },
    async fetchCompanies() {
      try {
        const response = await axios.get("/candidati/companies");
        const companies = (Array.isArray(response.data) ? response.data : []).sort((a, b) => b.id - a.id);
        this.companiesData = await this.attachCompanyLicenses(companies);
      } catch (error) {
        console.error("Eroare la obținerea datelor companiilor:", error);
      }
    },
    closeDialog() {
      this.editDialog = false;
      this.dialogError = "";
      this.saveLoading = false;
    },
  },
});
</script>

<style scoped>
.companies-module {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.controls-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.search-field {
  max-width: 320px;
}

.companies-grid {
  margin-top: 8px;
}

.company-card {
  position: relative;
  overflow: hidden;
  border: 1px solid var(--ncrm-color-border-default, rgba(0, 0, 0, 0.08)) !important;
  border-radius: var(--app-radius-base);
  height: 100%;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.05);
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
  cursor: pointer;
}

.company-card::before {
  content: "";
  position: absolute;
  inset: 0 0 auto 0;
  height: 4px;
  background: rgba(var(--v-theme-primary), 0.68);
}

.company-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 30px rgba(0, 0, 0, 0.09);
  border-color: var(--ncrm-color-border-strong, rgba(0, 0, 0, 0.12)) !important;
}

.company-card:focus-visible,
.company-card:focus-within {
  outline: 3px solid rgba(var(--v-theme-primary), 0.44);
  outline-offset: 2px;
  border-color: rgba(var(--v-theme-primary), 0.35) !important;
}

.company-card__content {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 22px 20px 18px;
}

.company-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.company-card__identity {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
}

.company-card__icon {
  width: 44px;
  height: 44px;
  flex: 0 0 44px;
  border-radius: var(--app-radius-base);
  background: rgba(var(--v-theme-primary), 0.12);
  color: rgb(var(--v-theme-primary));
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.company-card__heading {
  min-width: 0;
}

.company-card__name {
  font-size: 20px;
  font-weight: 700;
  line-height: 1.25;
  color: var(--app-text-strong);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.company-card__administrator {
  margin-top: 4px;
  font-size: 14px;
  line-height: 1.45;
  color: rgba(var(--v-theme-on-surface), 0.74);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.company-card__status {
  flex-shrink: 0;
}

.company-card__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.company-card__details {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.company-detail-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-width: 0;
}

.company-detail-row__label {
  flex: 0 0 auto;
  font-size: 13px;
  color: rgba(var(--v-theme-on-surface), 0.62);
}

.company-detail-row__value {
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--app-text-strong);
  text-align: right;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.company-card__license-panel {
  border: 1px solid var(--ncrm-color-border-subtle, rgba(0, 0, 0, 0.06));
  border-radius: var(--app-radius-base);
  background: var(--ncrm-color-surface-muted, rgba(var(--v-theme-surface), 0.92));
  padding: 14px 16px;
}

.company-card__section-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(var(--v-theme-on-surface), 0.56);
}

.company-card__license-package {
  margin-top: 6px;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.4;
  color: var(--app-text-strong);
}

.company-card__license-expiry {
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: rgba(var(--v-theme-on-surface), 0.72);
}

.company-card__actions {
  justify-content: flex-end;
  gap: 8px;
  padding: 10px 16px 14px;
}

.company-card__actions :deep(.v-btn) {
  text-transform: none;
  letter-spacing: 0;
}

.companies-table {
  margin-top: 12px;
  border-radius: var(--app-radius-base);
  overflow: hidden;
}

.companies-table :deep(th) {
  font-weight: 600;
}

.companies-table :deep(td) {
  vertical-align: middle;
  font-size: 14px;
}

.company-table__muted {
  color: rgba(var(--v-theme-on-surface), 0.72);
}

.table-actions {
  display: flex;
  gap: 4px;
}

@media (max-width: 959px) {
  .company-card__header {
    flex-direction: column;
  }

  .company-card__status {
    align-self: flex-start;
  }
}

@media (max-width: 600px) {
  .company-card__content {
    padding: 20px 16px 16px;
  }

  .company-detail-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .company-detail-row__value {
    text-align: left;
    white-space: normal;
    overflow: visible;
  }
}
</style>
